import { Injectable } from '@angular/core';
import { ExerciseSet } from '../models/exercise-set.interface';

interface Exercise {
  id: number;
  exercise: string;
  exerciseId?: string;
  allotmentId?: string;
  sets: ExerciseSet[];
  section?: 'warmup' | 'main' | 'cooldown';
  isMarkedForDeletion?: boolean;
}

interface DateItem {
  date: Date;
  formattedDate: string;
  dayName: string;
  isExpanded: boolean;
  exercises: Exercise[];
  isRestDay: boolean;
  restDayAllotmentId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExerciseValidationService {
  /**
   * Validate all exercises in the date list
   */
  validateAllExercises(dateList: DateItem[]): string[] {
    const errors: string[] = [];

    dateList.forEach((dateItem, dateIndex) => {
      // Skip rest days
      if (dateItem.isRestDay) {
        return;
      }

      // Skip dates in the past (read-only)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const itemDate = new Date(dateItem.date);
      itemDate.setHours(0, 0, 0, 0);

      if (itemDate < today) {
        return; // Skip validation for past dates
      }

      // Get active exercises (not marked for deletion)
      const activeExercises = dateItem.exercises.filter(
        (ex) => !ex.isMarkedForDeletion
      );

      // Check for empty workout days (no exercises or all exercises are empty)
      const hasValidExercises = activeExercises.some(
        (exercise) => exercise.exercise && exercise.exercise.trim() !== ''
      );

      if (!hasValidExercises) {
        errors.push(
          `${dateItem.dayName} (${dateItem.formattedDate}): Workout day must have at least one exercise.`
        );
        return;
      }

      // Validate each exercise
      activeExercises.forEach((exercise, exerciseIndex) => {
        // Skip empty exercises (no exercise name selected)
        if (!exercise.exercise || exercise.exercise.trim() === '') {
          return;
        }

        const sectionName = this.getSectionDisplayName(
          exercise.section || 'main'
        );
        const exerciseIdentifier = `${dateItem.dayName} (${dateItem.formattedDate}) - ${sectionName} - ${exercise.exercise}`;

        // Check if exercise has at least one valid set
        const hasValidSet = exercise.sets.some((set) => {
          const repsValue =
            typeof set.reps === 'string'
              ? set.reps.trim()
              : set.reps !== null && set.reps !== undefined
              ? String(set.reps).trim()
              : '';

          // Check for time - prioritize separate minute/second fields over time string
          let hasValidTime = false;
          const minutes = Number(set.timeMinutes) || 0;
          const seconds = Number(set.timeSeconds) || 0;
          hasValidTime = minutes > 0 || seconds > 0;

          // If no separate fields, check time string as fallback
          if (!hasValidTime && typeof set.time === 'string') {
            const timeValue = set.time.trim();
            hasValidTime = !!(
              timeValue &&
              timeValue !== '0' &&
              timeValue !== '00:00'
            );
          }

          const hasValidReps =
            repsValue && repsValue !== '0' && repsValue !== '';
          return hasValidReps || hasValidTime;
        });

        if (!hasValidSet) {
          errors.push(
            `${exerciseIdentifier}: Must have at least one set with either reps or time specified.`
          );
        }

        // Validate individual sets for consistency and completeness
        exercise.sets.forEach((set, setIndex) => {
          const repsValue =
            typeof set.reps === 'string'
              ? set.reps.trim()
              : set.reps !== null && set.reps !== undefined
              ? String(set.reps).trim()
              : '';

          let hasTime = false;
          const minutes = Number(set.timeMinutes) || 0;
          const seconds = Number(set.timeSeconds) || 0;
          hasTime = minutes > 0 || seconds > 0;

          if (!hasTime && typeof set.time === 'string') {
            const timeValue = set.time.trim();
            hasTime = !!(
              timeValue &&
              timeValue !== '0' &&
              timeValue !== '00:00'
            );
          }

          const weightsValue =
            typeof set.weights === 'string'
              ? set.weights.trim()
              : set.weights !== null && set.weights !== undefined
              ? String(set.weights).trim()
              : '';

          const hasReps = repsValue && repsValue !== '0' && repsValue !== '';
          const hasWeights =
            weightsValue && weightsValue !== '0' && weightsValue !== '';

          // If a set has only weights but no reps or time, it's incomplete
          if (hasWeights && !hasReps && !hasTime) {
            errors.push(
              `${exerciseIdentifier} - Set ${set.setNumber}: Cannot have weights without reps or time. Please specify either reps or time.`
            );
          }

          // If both reps and time are entered in the same set, show error
          if (hasReps && hasTime) {
            errors.push(
              `${exerciseIdentifier} - Set ${set.setNumber}: Please enter either reps or time, not both.`
            );
          }
        });
      });
    });

    return errors;
  }

  /**
   * Check if exercise set is completed (based on status field)
   */
  isSetCompleted(set: ExerciseSet): boolean {
    return set.status === 'completed';
  }

  /**
   * Check if exercise already exists (has valid exercise name)
   */
  hasExistingExercise(exercise: Exercise): boolean {
    return !!(exercise.exercise && exercise.exercise.trim() !== '');
  }

  /**
   * Get section display name for validation messages
   * @param section - Section type
   * @returns Display name
   */
  private getSectionDisplayName(
    section: 'warmup' | 'main' | 'cooldown'
  ): string {
    const sectionNames = {
      warmup: 'Warm Up',
      main: 'Main Exercise',
      cooldown: 'Cool Down',
    };
    return sectionNames[section];
  }

  /**
   * Validate exercise name is not empty
   */
  isExerciseNameValid(exerciseName: string): boolean {
    return !!(exerciseName && exerciseName.trim() !== '');
  }

  /**
   * Validate that at least one set has data
   */
  hasValidSets(sets: ExerciseSet[]): boolean {
    return sets.some((set) => this.isSetCompleted(set));
  }

  /**
   * Check if all required fields are filled for a complete exercise
   */
  isExerciseComplete(exercise: Exercise): boolean {
    return (
      this.hasExistingExercise(exercise) && this.hasValidSets(exercise.sets)
    );
  }

  /**
   * Check if all exercises in an array are complete
   * @param exercises - Array of exercises to validate
   * @returns Whether all exercises are complete
   */
  areAllExercisesComplete(exercises: Exercise[]): boolean {
    if (!exercises || exercises.length === 0) return false;

    const activeExercises = exercises.filter((ex) => !ex.isMarkedForDeletion);
    return activeExercises.every((exercise) =>
      this.isExerciseComplete(exercise)
    );
  }

  /**
   * Get count of exercises that have at least one complete set
   * @param exercises - Array of exercises to check
   * @returns Number of exercises with complete sets
   */
  getExercisesWithCompleteSetsCount(exercises: Exercise[]): number {
    if (!exercises || exercises.length === 0) return 0;

    const activeExercises = exercises.filter((ex) => !ex.isMarkedForDeletion);
    return activeExercises.filter((exercise) => this.hasCompleteSets(exercise))
      .length;
  }

  /**
   * Check if an exercise has at least one complete set
   * @param exercise - Exercise to check
   * @returns Whether exercise has complete sets
   */
  hasCompleteSets(exercise: Exercise): boolean {
    if (!exercise || !exercise.sets || exercise.sets.length === 0) return false;
    return exercise.sets.some((set) => this.isSetCompleted(set));
  }
}
